import { useEffect, useRef, useState } from 'react'

import { motion } from 'framer-motion'

const MILESTONES = [0, 25, 50, 75, 98, 100]

interface VideoPlayerProps {
  src: string
  onPercentageWatchedMilestone?(milestone: number, video: HTMLVideoElement): void
  disableAdvancedControls?: boolean
}

export default function VideoPlayer({
  src,
  onPercentageWatchedMilestone,
  disableAdvancedControls = false,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const milestonesRef = useRef<Set<number>>(new Set())
  const callbackRef = useRef(onPercentageWatchedMilestone)
  const [videoLoaded, setVideoLoaded] = useState(false)

  callbackRef.current = onPercentageWatchedMilestone

  useEffect(() => {
    milestonesRef.current.clear()
  }, [src])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => {
      if (!video.duration || !callbackRef.current) return

      for (const milestone of MILESTONES) {
        if (milestonesRef.current.has(milestone)) continue

        const progress = Math.round((video.currentTime / video.duration) * 100)
        if (progress >= milestone) {
          milestonesRef.current.add(milestone)
          callbackRef.current(milestone, video)
        }
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => video.removeEventListener('timeupdate', handleTimeUpdate)
  }, [])

  return (
    <motion.video
      ref={videoRef}
      controls
      controlsList={disableAdvancedControls ? 'nodownload' : undefined}
      disablePictureInPicture={disableAdvancedControls}
      src={src}
      initial={{ opacity: 0 }}
      animate={{ opacity: videoLoaded ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      onLoadedData={() => setVideoLoaded(true)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  )
}
